import React from "react";
import { LuChevronDown } from "react-icons/lu";
import { newRouteId } from "../utils/printBridge";
import { PrinterTile, StatusDot, printerStatusLabel } from "./PrinterRowParts";

const MAIN = "main";
const RECEIPT_COPY = "receipt-copy";
const ALL = "all";
const MULTI = "multi"; // a stored route with several categories; kept as is
const catValue = (id) => `cat:${id}`;

// One card per printer, each with a dropdown that says what it prints:
//   Main receipt printer   the customer receipt (all plans)
//   Receipt copy           a second customer receipt (Pro)
//   All items / category   kitchen or bar slip, no prices (Pro)
// The parent owns `selected` (main printer) and `routes` (Pro extras) and saves
// them with its Save button. A printer has one assignment at a time.
function PrinterAssignments({
  printers,
  loading,
  selected,
  setSelected,
  routes,
  setRoutes,
  categories,
  canAssignExtras,
}) {
  const known = new Set(printers.map((p) => p.name));
  const missingNames = loading
    ? []
    : [...new Set([selected, ...routes.map((r) => r.printerName)])].filter(
        (name) => name && !known.has(name)
      );
  const rows = [
    ...printers,
    ...missingNames.map((name) => ({ name, missing: true })),
  ];

  const routeFor = (name) => routes.find((r) => r.printerName === name);

  const categoryName = (id) =>
    categories.find((c) => Number(c.id) === Number(id))?.cat_name || `Category ${id}`;

  const valueFor = (name) => {
    if (selected === name) return MAIN;
    const route = canAssignExtras && routeFor(name);
    if (!route) return "";
    if (route.type === "receipt") return RECEIPT_COPY;
    if (route.items === "all") return ALL;
    return route.categoryIds.length === 1 ? catValue(route.categoryIds[0]) : MULTI;
  };

  // The printer already holding this category in an "assigned" slip route.
  const categoryOwner = (id, name) =>
    routes.find(
      (r) =>
        r.printerName !== name &&
        r.type === "slip" &&
        r.items === "assigned" &&
        r.categoryIds.includes(id)
    )?.printerName;

  const assign = (name, value) => {
    if (value === MULTI) return;
    const withoutThis = (prev) => prev.filter((r) => r.printerName !== name);

    if (value === "" || value === MAIN) {
      setRoutes(withoutThis);
      if (value === MAIN) setSelected(name);
      else if (selected === name) setSelected("");
      return;
    }

    if (selected === name) setSelected("");
    const next =
      value === RECEIPT_COPY
        ? { type: "receipt", items: "all", categoryIds: [] }
        : value === ALL
        ? { type: "slip", items: "all", categoryIds: [] }
        : { type: "slip", items: "assigned", categoryIds: [Number(value.slice(4))] };

    setRoutes((prev) => {
      const existing = prev.find((r) => r.printerName === name);
      const route = {
        id: existing?.id || newRouteId(),
        printerName: name,
        printOn: existing?.printOn || { draft: false, paid: true },
        ...next,
      };
      return existing
        ? prev.map((r) => (r === existing ? route : r))
        : [...prev, route];
    });
  };

  const togglePrintOn = (name, key) =>
    setRoutes((prev) =>
      prev.map((r) =>
        r.printerName === name
          ? { ...r, printOn: { ...r.printOn, [key]: !r.printOn[key] } }
          : r
      )
    );

  return (
    <ul className="flex flex-col gap-3 mb-3">
      {rows.map((printer, index) => {
        const value = valueFor(printer.name);
        const route = canAssignExtras ? routeFor(printer.name) : null;
        const showPrintOn = route && value !== MAIN;
        const selectId = `assign-${index}`;
        return (
          <li
            key={printer.name}
            className={`rounded-xl border bg-white p-4 shadow-sm ${
              value === MAIN ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-200"
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <PrinterTile online={!!printer.isAvailable} />
              <div className="flex-1 min-w-[8rem]">
                <div className="font-semibold text-slate-900 break-words">
                  {printer.name}
                </div>
                {printer.missing ? (
                  <div className="text-xs text-amber-700">Not found on this computer.</div>
                ) : (
                  <StatusDot online={printer.isAvailable}>
                    {printerStatusLabel(printer)}
                  </StatusDot>
                )}
              </div>
              <div className="w-full sm:w-56">
                <label
                  htmlFor={selectId}
                  className="block text-sm text-slate-600 mb-1"
                >
                  Assign Restaurant Category
                </label>
                <div className="relative">
                  <select
                    id={selectId}
                    value={value}
                    onChange={(e) => assign(printer.name, e.target.value)}
                    className="w-full appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-9 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Not assigned</option>
                    <option value={MAIN}>Main receipt printer</option>
                    {canAssignExtras && (
                      <>
                        <option value={RECEIPT_COPY}>Receipt copy (with prices)</option>
                        <option value={ALL}>All items (slip)</option>
                        {value === MULTI && (
                          <option value={MULTI}>
                            {route.categoryIds.map(categoryName).join(", ")}
                          </option>
                        )}
                        {categories.map((category) => {
                          const id = Number(category.id);
                          const owner = categoryOwner(id, printer.name);
                          return (
                            <option key={id} value={catValue(id)} disabled={!!owner}>
                              {category.cat_name}
                              {owner && ` (used by ${owner})`}
                            </option>
                          );
                        })}
                      </>
                    )}
                  </select>
                  <LuChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>

            {showPrintOn && (
              <div className="mt-3 border-t border-gray-100 pt-3">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-700">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Print on
                  </span>
                  {[
                    ["draft", "When I print a draft"],
                    ["paid", "After a paid sale"],
                  ].map(([key, text]) => (
                    <label key={key} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={route.printOn[key]}
                        onChange={() => togglePrintOn(printer.name, key)}
                      />
                      {text}
                    </label>
                  ))}
                </div>
                {!route.printOn.draft && !route.printOn.paid && (
                  <p className="text-xs text-red-600 mt-1">Pick at least one.</p>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default PrinterAssignments;
